// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-7 — what each partner holds (owner, 2026-11-03):
//   balance = earned from CLOSED months + shared income × % + direct income + released held money × % − payouts
// Negative = paid up front. Open / running months are pending (shown, not counted). Held money (the operating-bucket
// remainder of closed months) stays in its own pool until released. Entries are never deleted — a mistake is voided
// with a reason. Every write is audit-logged server-side.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import { HandCoins, Plus, Unlock } from 'lucide-react'
import AppButton from '../../../components/ui/AppButton'
import AppDialog from '../../../components/ui/AppDialog'
import ActionMenu from '../../../components/ui/ActionMenu'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import DateInput, { localTodayIso, showDate } from '../../../components/ui/DateInput'
import LoadState from '../../../components/ui/LoadState'
import Notice from '../../../components/ui/Notice'
import Section, { StatTile } from '../../../components/ui/Section'
import StatusChip from '../../../components/ui/StatusChip'
import SummaryLines from '../../../components/ui/SummaryLines'
import { useToast } from '../../../components/ui/Toast'
import { AppTextField, ChoiceToggle, MoneyField } from '../../../components/ui/FormFields'
import { DataGrid, type DataGridColumn } from '../../../components/DataGrid'
import { friendlyError } from '../../../api/errors'
import {
  createPartnerEntry, financeKeys, getPartners, voidPartnerEntry,
  type Partner, type PartnerEntry, type PartnerEntryInput, type PartnerEntryKind, type PartnerHolding,
} from '../../../api/finance'
import { money } from './FinanceTables'
import { monthLabel } from './CostItemDialogs'

export const PARTNER_LABEL: Record<Partner, string> = { blue_dev: 'Blue Dev', owl: 'Owl' }
const PARTNER_COLOR: Record<Partner, string> = { blue_dev: '#6d28d9', owl: '#0f766e' }
export const ENTRY_LABEL: Record<PartnerEntryKind, string> = {
  income_shared: 'Income — both partners', income_direct: 'Income — one partner', payout: 'Payout', release: 'Held released',
}

/** Which dialog is open: a payout, partner income (shared or direct), or a release of held money. */
export type EntryMode = 'payout' | 'income' | 'release'

const signedMoney = (n: number) => <Box component="span" sx={{ color: n < 0 ? 'error.main' : undefined }}>{money(n)}</Box>

function HoldingLines({ h }: { h: PartnerHolding }) {
  return (
    <SummaryLines lines={[
      { key: 'earned', label: 'Earned from closed months', value: signedMoney(h.earned_usd) },
      { key: 'shared', label: '+ Shared income (their %)', value: money(h.shared_income_usd) },
      { key: 'direct', label: '+ Direct income', value: money(h.direct_income_usd) },
      { key: 'released', label: '+ Held money released (their %)', value: money(h.released_usd) },
      { key: 'paid', label: '− Paid out', value: money(h.paid_usd) },
      { key: 'bal', label: '= Balance', value: signedMoney(h.balance_usd), strong: true },
      { key: 'pend', label: 'Pending — months not closed yet (not counted)', value: signedMoney(h.pending_usd), muted: true },
    ]} />
  )
}

export function PartnerEntryDialog({ mode, open, devSplitPct, heldBalance, onClose }: {
  mode: EntryMode; open: boolean; devSplitPct: number; heldBalance?: number; onClose: () => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const [partner, setPartner] = useState<Partner | 'both'>(mode === 'income' ? 'both' : 'owl')
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(localTodayIso())
  const [title, setTitle] = useState('')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')

  const kind: PartnerEntryKind = mode === 'payout' ? 'payout' : mode === 'release' ? 'release' : partner === 'both' ? 'income_shared' : 'income_direct'
  const shared = kind === 'income_shared' || kind === 'release'
  const amt = amount.trim() === '' ? NaN : Number(amount)
  const amtOk = Number.isFinite(amt) && amt > 0 && amt <= 1_000_000 && (mode !== 'release' || heldBalance === undefined || amt <= heldBalance)
  const valid = amtOk && date !== '' && date <= localTodayIso() && title.trim().length >= 3
  const blue = Number.isFinite(amt) ? Math.round(amt * devSplitPct) / 100 : 0

  const save = useMutation({
    mutationFn: () => createPartnerEntry({
      kind, partner: shared ? null : partner as Partner, amount_usd: Math.round(amt * 100) / 100, entry_date: date,
      title: title.trim(), reference: reference.trim() || null, notes: notes.trim() || null,
    } satisfies PartnerEntryInput),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financeKeys.all })
      toast.show(`${ENTRY_LABEL[kind]} of ${money(amt)} recorded`, 'success')
      onClose()
    },
  })

  const heading = mode === 'payout' ? 'Record a payout' : mode === 'release' ? 'Release held money' : 'Add partner income'
  return (
    <AppDialog open={open} onClose={onClose} busy={save.isPending} title={heading}
      actions={<>
        <AppButton onClick={onClose} disabled={save.isPending}>Cancel</AppButton>
        <AppButton variant="contained" disabled={!valid || save.isPending} onClick={() => { save.reset(); save.mutate() }}>
          {save.isPending ? 'Saving…' : 'Save'}
        </AppButton>
      </>}>
      <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
        {save.isError && <Notice severity="error">{friendlyError(save.error)}</Notice>}
        {mode === 'income' && (
          <Box>
            <ChoiceToggle ariaLabel="Goes to" value={partner}
              options={[{ value: 'both', label: `Both — ${devSplitPct}/${100 - devSplitPct}` }, { value: 'blue_dev', label: 'Blue Dev only' }, { value: 'owl', label: 'Owl only' }]}
              onChange={v => setPartner(v)} />
            <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75 }}>
              Partner income is not revenue: no reserve and no costs come off it. Income a customer pays (setup fees, extra billing) belongs in Income &amp; credits instead.
            </Box>
          </Box>
        )}
        {mode === 'payout' && (
          <ChoiceToggle ariaLabel="Paid to" value={partner === 'both' ? 'owl' : partner}
            options={[{ value: 'blue_dev', label: 'Blue Dev' }, { value: 'owl', label: 'Owl' }]} onChange={v => setPartner(v)} />
        )}
        {mode === 'release' && heldBalance !== undefined && (
          <Box sx={{ fontSize: 13 }}>Held balance: <b>{money(heldBalance)}</b>. A release is split {devSplitPct}% Blue Dev / {100 - devSplitPct}% Owl.</Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start' }}>
          <MoneyField label="Amount" required value={amount} onChange={setAmount} error={amount !== '' && !amtOk}
            helperText={amount !== '' && !amtOk && mode === 'release' ? 'More than the held balance'
              : shared && Number.isFinite(amt) && amt > 0 ? `Blue Dev ${money(blue)} · Owl ${money(Math.round((amt - blue) * 100) / 100)}` : ' '} />
          <Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>{mode === 'payout' ? 'Date paid' : 'Date'}</Box>
            <DateInput label={mode === 'payout' ? 'Date paid' : 'Date'} value={date} onChange={v => setDate(v ?? '')} />
            {date > localTodayIso() && <Box sx={{ fontSize: 12, color: 'error.main' }}>Not in the future</Box>}
          </Box>
        </Box>
        <AppTextField label="Title" required value={title} onChange={e => setTitle(e.target.value)} inputProps={{ maxLength: 200 }}
          placeholder={mode === 'payout' ? 'e.g. October draw' : mode === 'release' ? 'e.g. Q3 held money' : 'e.g. Consulting for XYZ'} />
        <AppTextField label="Reference" value={reference} onChange={e => setReference(e.target.value)} inputProps={{ maxLength: 400 }}
          placeholder="Transfer / invoice number" helperText="Numbers or links only — never patient details." />
        <AppTextField label="Notes" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2} inputProps={{ maxLength: 400 }} />
      </Box>
    </AppDialog>
  )
}

export default function PartnersPanel() {
  const qc = useQueryClient()
  const toast = useToast()
  const q = useQuery({ queryKey: financeKeys.partners, queryFn: getPartners })
  const [dialog, setDialog] = useState<EntryMode | null>(null)
  const [voiding, setVoiding] = useState<PartnerEntry | null>(null)
  const voidIt = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => voidPartnerEntry(id, reason),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: financeKeys.all }); toast.show('Entry voided', 'success'); setVoiding(null) },
  })

  const cols: DataGridColumn<PartnerEntry>[] = [
    { key: 'date', header: 'Date', minWidth: 110, value: e => e.entry_date, render: e => showDate(e.entry_date) },
    {
      key: 'kind', header: 'What', minWidth: 210, value: e => `${ENTRY_LABEL[e.kind]} ${e.title}`,
      render: e => (
        <Box sx={{ opacity: e.voided_at ? 0.55 : 1 }}>
          <b>{e.title}</b>
          <Box sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            {ENTRY_LABEL[e.kind]}{e.partner ? ` · ${PARTNER_LABEL[e.partner]}` : e.dev_split_pct !== null ? ` · ${e.dev_split_pct}/${100 - e.dev_split_pct}` : ''}
            {e.reference ? ` · ${e.reference}` : ''}
          </Box>
        </Box>
      ),
    },
    { key: 'amount', header: 'Amount', align: 'right', value: e => e.amount_usd, render: e => money(e.amount_usd) },
    { key: 'blue', header: 'Blue Dev', align: 'right', value: e => e.blue_usd, render: e => (e.voided_at ? '—' : signedMoney(e.blue_usd)) },
    { key: 'owl', header: 'Owl', align: 'right', value: e => e.owl_usd, render: e => (e.voided_at ? '—' : signedMoney(e.owl_usd)) },
    {
      key: 'state', header: 'State', noFilter: true, value: e => (e.voided_at ? 'void' : 'counted'),
      render: e => e.voided_at
        ? <Box><StatusChip status="void" tone="grey" label="Void" />
            <Box sx={{ fontSize: 11, color: 'text.secondary' }}>{e.void_reason}</Box></Box>
        : <StatusChip status="counted" tone="green" label="Counted" />,
    },
    { key: 'by', header: 'By', hidden: true, value: e => e.created_by },
    {
      key: 'actions', header: '', noFilter: true, width: 56,
      render: e => e.voided_at ? null : <ActionMenu label={`Actions for ${e.title}`} actions={[
        { key: 'void', label: 'Void…', hint: 'Entered by mistake — it stops counting, the record stays', danger: true, onClick: () => { voidIt.reset(); setVoiding(e) } },
      ]} />,
    },
  ]

  return (
    <LoadState query={q} what="the partner balances" isEmpty={() => false} empty={null}>
      {data => {
        const blue = data.partners.find(p => p.partner === 'blue_dev')!
        const owl = data.partners.find(p => p.partner === 'owl')!
        return (
          <>
            <Section title="Partner balances"
              subtitle={<>What each partner holds right now: earned from closed months, plus partner income and released held money, minus what was paid out.
                A <b>negative</b> balance means the partner was paid up front. {data.pending_months.length > 0 &&
                  <>Pending ({data.pending_months.map(monthLabel).join(', ')}) is not counted until the month closes.</>}</>}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }}>
                <StatTile label="Blue Dev balance" value={signedMoney(blue.balance_usd)} color={PARTNER_COLOR.blue_dev}
                  hint={blue.balance_usd < 0 ? 'paid up front' : `pending ${money(blue.pending_usd)}`} />
                <StatTile label="Owl balance" value={signedMoney(owl.balance_usd)} color={PARTNER_COLOR.owl}
                  hint={owl.balance_usd < 0 ? 'paid up front' : `pending ${money(owl.pending_usd)}`} />
                <StatTile label="Held (bucket left over)" value={money(data.held.balance_usd)}
                  hint={`${money(data.held.accrued_usd)} put on hold · ${money(data.held.released_usd)} released${data.held.pending_usd ? ` · ${money(data.held.pending_usd)} pending` : ''}`} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <AppButton variant="contained" startIcon={<HandCoins size={14} />} onClick={() => setDialog('payout')}>Record payout</AppButton>
                <AppButton startIcon={<Plus size={14} />} onClick={() => setDialog('income')}>Add partner income</AppButton>
                <AppButton startIcon={<Unlock size={14} />} disabled={data.held.balance_usd <= 0} onClick={() => setDialog('release')}>Release held</AppButton>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Box><Box sx={{ fontWeight: 700, fontSize: 13, color: PARTNER_COLOR.blue_dev, mb: 0.5 }}>Blue Dev</Box><HoldingLines h={blue} /></Box>
                <Box><Box sx={{ fontWeight: 700, fontSize: 13, color: PARTNER_COLOR.owl, mb: 0.5 }}>Owl</Box><HoldingLines h={owl} /></Box>
              </Box>
            </Section>
            <Section title="Ledger" subtitle="Every payout, partner income and release, newest first. A mistake is voided with a reason — nothing is deleted.">
              {data.entries.length === 0
                ? <Box sx={{ fontSize: 12.5, color: 'text.secondary', py: 1.5 }}>Nothing recorded yet.</Box>
                : <DataGrid rows={data.entries} columns={cols} storageKey="finance-v2-partner-ledger" getRowKey={e => e.id} dense />}
            </Section>
            {dialog && (
              <PartnerEntryDialog open mode={dialog} devSplitPct={data.dev_split_pct} heldBalance={data.held.balance_usd} onClose={() => setDialog(null)} />
            )}
            <ConfirmDialog open={!!voiding} tone="danger" title={`Void ${voiding?.title ?? ''}?`}
              body="It stops counting in every balance. The record stays in the ledger, marked void, with your reason."
              requireReason={{ label: 'Why', minLength: 5 }} confirmLabel="Void" busy={voidIt.isPending}
              error={voidIt.isError ? friendlyError(voidIt.error) : null} onCancel={() => { if (!voidIt.isPending) setVoiding(null) }}
              onConfirm={({ reason }) => { if (voiding) voidIt.mutate({ id: voiding.id, reason }) }} />
          </>
        )
      }}
    </LoadState>
  )
}
